import { CardHeader, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import StyledTextFieldReadOnly from "components/StyledTextFieldReadOnly";
import { intlFormatPrice, isPartReturnRequested } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { JobVisitType } from "models/JobVisitType";
import { UsedPart } from "models/usedPart";
import { StockStore } from "operations/schema/schema";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SmallTotalTimeComponent } from "../times/SmallTotalTimeComponent";
import { TotalTimeComponent } from "../times/TotalTimeComponent";

type JobVisitSummaryProps = {
  jobVisit: JobVisitType;
};
export const JobVisitSummary: FC<JobVisitSummaryProps> = (props: JobVisitSummaryProps) => {
  const { jobVisit } = props;
  const { usedParts, workTimes, solutionDescription, travelTimes, extras } = jobVisit;
  const intl = useIntl();
  const allowSalesPrice = technicianSettings.syncCostPrice || technicianSettings.syncCostPrice;

  const travelEnd = travelTimes[travelTimes.length - 1]?.stopTime
    ? new Date(travelTimes[travelTimes.length - 1].stopTime!)
    : null;

  const jobUsedParts = usedParts
    .filter(
      (jobPart: UsedPart) =>
        jobPart.part.stockStore !== StockStore.Other && !isPartReturnRequested(jobPart.part),
    )
    .map((jobPart: UsedPart) => {
      return {
        quantity: jobPart.part.quantity,
        articleNo: jobPart.part.articleNo,
        stockId: jobPart.part.stockId,
        stockStore: jobPart.part.stockStore,
        description: jobPart.part.description,
        storeLabel: jobPart.part.stockStore === StockStore.Nonstock ? jobPart.part.storeLabel : "",
        salesPrice: jobPart.part.salesPrice ?? 0,
      };
    });

  const jobRequestedParts = usedParts
    .filter((jobPart: UsedPart) => jobPart.part.stockStore === StockStore.Other)
    .map((jobPart: UsedPart) => {
      return {
        quantity: jobPart.part.quantity,
        articleNo: jobPart.part.articleNo,
        stockId: jobPart.part.stockId,
        stockStore: jobPart.part.stockStore,
        description: jobPart.part.description,
        salesPrice: jobPart.part.salesPrice ?? 0,
      };
    });

  const totalSalesPrice =
    jobUsedParts.reduce((sum, jobPart) => sum + jobPart.salesPrice * (jobPart.quantity ?? 0), 0) +
    jobRequestedParts.reduce(
      (sum, jobPart) => sum + jobPart.salesPrice * (jobPart.quantity ?? 0),
      0,
    );

  const totalExtrasPrice = extras.reduce(
    (sum, extra) => sum + (extra.salesPrice ?? 0) * (extra.quantity ?? 0),
    0,
  );

  const jobReturnedParts = usedParts
    .filter(
      (jobPart: UsedPart) =>
        jobPart.part.stockStore !== StockStore.Other && isPartReturnRequested(jobPart.part),
    )
    .map((jobPart: UsedPart) => {
      return {
        quantity: jobPart.part.quantity,
        articleNo: jobPart.part.articleNo,
        stockId: jobPart.part.stockId,
        stockStore: jobPart.part.stockStore,
        description: jobPart.part.description,
        storeLabel: jobPart.part.stockStore === StockStore.Nonstock ? jobPart.part.storeLabel : "",
      };
    });

  return (
    <>
      <Card elevation={1}>
        <CardHeader
          title={intl.formatMessage({ id: "visit.visitInformation" })}
          sx={{ paddingBottom: "0px" }}
        />
        <CardContent sx={{ paddingTop: "0px" }}>
          <List dense>
            {travelEnd !== null && (
              <ListItem key="Travel time">
                <ListItemText
                  key="Travel time text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "times.travelTime" })}:`}
                  secondary={<TotalTimeComponent times={travelTimes} />}
                />
              </ListItem>
            )}
            <ListItem key="Work time">
              <ListItemText
                key="Work time text"
                primaryTypographyProps={{ variant: "body1" }}
                secondaryTypographyProps={{
                  variant: "subtitle2",
                  color: "textPrimary",
                }}
                primary={`${intl.formatMessage({ id: "times.workTime" })}:`}
                secondary={<TotalTimeComponent times={workTimes} />}
              />
            </ListItem>
            {solutionDescription !== "" && (
              <ListItem>
                <StyledTextFieldReadOnly
                  label={intl.formatMessage({ id: "job.solutionDescription" })}
                  multiline
                  rows={10}
                  defaultValue={solutionDescription}
                  InputProps={{
                    readOnly: true,
                    disabled: true,
                  }}
                />
              </ListItem>
            )}

            {allowSalesPrice && jobUsedParts?.length > 0 && (
              <ListItem>
                <strong>
                  <FormattedMessage id="visit.totalPartsPrice" />
                </strong>
                : {intlFormatPrice(intl, totalSalesPrice)}
              </ListItem>
            )}

            {jobUsedParts.length > 0 && (
              <ListItem key="visit.usedParts">
                <ListItemText
                  key="Used parts text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "visit.usedParts" })}:`}
                  secondary={jobUsedParts.map((usedPart, index) => {
                    return (
                      <div key={index}>
                        <div className="truncated">
                          {`${usedPart.quantity} x ${usedPart.description}${
                            usedPart.storeLabel ? ` (${usedPart.storeLabel})` : ""
                          }`}
                        </div>
                        {allowSalesPrice && !!usedPart.salesPrice && (
                          <Typography variant="body2" color="textSecondary">
                            <FormattedMessage id="general.salesPrice" />:{" "}
                            {intlFormatPrice(intl, usedPart.salesPrice)}
                          </Typography>
                        )}
                      </div>
                    );
                  })}
                />
              </ListItem>
            )}
            {jobRequestedParts.length > 0 && (
              <ListItem key="Requested parts">
                <ListItemText
                  key="Requested parts text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "part.requestedParts" })}:`}
                  secondary={jobRequestedParts.map((requestedPart, index) => (
                    <div key={index}>
                      <div className="truncated">
                        {`${requestedPart.quantity} x ${requestedPart.description}`}
                      </div>
                      {allowSalesPrice && !!requestedPart.salesPrice && (
                        <Typography variant="body2" color="textSecondary">
                          <FormattedMessage id="general.salesPrice" />:{" "}
                          {intlFormatPrice(intl, requestedPart.salesPrice)}
                        </Typography>
                      )}
                    </div>
                  ))}
                />
              </ListItem>
            )}
            {jobReturnedParts.length > 0 && (
              <ListItem key="Returned parts">
                <ListItemText
                  key="Returned parts text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "part.returnedParts" })}:`}
                  secondary={jobReturnedParts.map((returnedParts, index) => (
                    <div className="truncated" key={index}>
                      {`${returnedParts.quantity} x ${returnedParts.description}`}
                    </div>
                  ))}
                />
              </ListItem>
            )}

            {totalExtrasPrice > 0 && extras.length > 0 && (
              <ListItem>
                <strong>
                  <FormattedMessage id="visit.totalExtrasPrice" />
                </strong>
                : {intlFormatPrice(intl, totalExtrasPrice)}
              </ListItem>
            )}
            {extras.length > 0 && (
              <ListItem key="Extras">
                <ListItemText
                  key="Extras text"
                  primaryTypographyProps={{ variant: "body1" }}
                  secondaryTypographyProps={{
                    variant: "subtitle2",
                    color: "textPrimary",
                  }}
                  primary={`${intl.formatMessage({ id: "job.extras" })}:`}
                  secondary={extras.map((extra, index) => (
                    <div key={index}>
                      <div className="truncated">{`${extra.id} - ${extra.type}`}</div>
                      {extra.description && (
                        <Typography variant="body2" color="textSecondary">
                          {extra.description}
                        </Typography>
                      )}
                      {extra.quantity && (
                        <Typography variant="body2" color="textSecondary">
                          <FormattedMessage id="general.quantity" />: {extra.quantity}
                        </Typography>
                      )}
                      {!!extra.salesPrice && extra.salesPrice > 0 && (
                        <Typography variant="body2" color="textSecondary">
                          <FormattedMessage id="general.salesPrice" />:{" "}
                          {intlFormatPrice(intl, extra.salesPrice)}
                        </Typography>
                      )}
                      {extra.isTime && !!extra.stopTime && (
                        <SmallTotalTimeComponent
                          startDate={extra.startTime}
                          stopDate={extra.stopTime}
                        />
                      )}
                    </div>
                  ))}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </>
  );
};
